// The fleet half of the API client: meters, readings, service history,
// disposal and the service-due list.
//
// Its own module rather than more of schedulingApi.ts, matching the server's
// split into routes/fleet.ts — this is a different subject with its own three
// tables, and the file it would otherwise join is already long.
import { ApiError, request } from './api';
import type { Resource } from './schedulingApi';

export type MeterUnit = 'km' | 'hours';
export type ServiceDueStatus = 'overdue' | 'due_soon' | 'ok';

export interface Meter {
  id: string;
  resourceId: string;
  label: string;
  unit: MeterUnit;
  /** Null when the meter has never been read, which is not the same as zero. */
  currentReading: number | null;
  readingUpdatedAt: string | null;
  isPrimary: boolean;
}

/** A meter on the yard-walk screen, carrying its machine's name. */
export interface BulkMeter extends Meter {
  resourceName: string;
  resourceIdentifier: string | null;
}

export interface MeterReading {
  id: string;
  meterId: string;
  reading: number;
  readingDate: string;
  /** The name as it was at the time — not a live join. */
  enteredByName: string;
  enteredById: string | null;
  createdAt: string;
}

export interface ServiceRecord {
  id: string;
  resourceId: string;
  serviceDate: string;
  description: string;
  reading: number | null;
  nextDueReading: number | null;
  nextDueDate: string | null;
  provider: string | null;
  notes: string | null;
  isService: boolean;
  createdByName: string;
  createdById: string | null;
  createdAt: string;
}

export interface Thresholds {
  warnKm: number;
  warnHours: number;
  leadDays: number;
}

export interface AssetDue extends Resource {
  serviceDueStatus: ServiceDueStatus | null;
  primaryMeterUnit: MeterUnit | null;
  primaryMeterReading: number | null;
}

/** One meter whose entered reading is lower than the one already recorded. */
export interface Regression {
  meterId: string;
  label: string;
  current: number;
  entered: number;
  unit: MeterUnit;
}

/**
 * Pulls the regression list out of a refused save.
 *
 * The same shape as `conflictsFrom` in schedulingApi: the server puts the
 * detail in `details`, and one reader for it means no screen has to know how
 * an ApiError is built.
 */
export function regressionsFrom(error: unknown): Regression[] {
  if (!(error instanceof ApiError) || error.code !== 'reading_regression') return [];
  const details = error.details as { regressions?: Regression[] } | undefined;
  return details?.regressions ?? [];
}

export const fleet = {
  listMeters: (resourceId: string) =>
    request<{ meters: Meter[] }>(`/resources/${resourceId}/meters`),
  createMeter: (resourceId: string, body: Record<string, unknown>) =>
    request<{ meter: Meter }>(`/resources/${resourceId}/meters`, { method: 'POST', body }),
  updateMeter: (resourceId: string, meterId: string, body: Record<string, unknown>) =>
    request<{ meter: Meter }>(`/resources/${resourceId}/meters/${meterId}`, {
      method: 'PATCH',
      body,
    }),
  deleteMeter: (resourceId: string, meterId: string) =>
    request<void>(`/resources/${resourceId}/meters/${meterId}`, { method: 'DELETE' }),

  readings: (resourceId: string, meterId: string) =>
    request<{ readings: MeterReading[] }>(
      `/resources/${resourceId}/meters/${meterId}/readings`
    ),

  /** Every meter in the yard, for the one-save walk-around screen. */
  bulkMeters: () =>
    request<{ meters: BulkMeter[]; thresholds: Thresholds; today: string }>(
      '/resources/readings'
    ),
  saveReadings: (entries: { meterId: string; reading: number }[], allowRegression = false) =>
    request<{ saved: number; regressions: Regression[] }>('/resources/readings', {
      method: 'POST',
      body: { entries, allowRegression },
    }),

  serviceRecords: (resourceId: string) =>
    request<{ serviceRecords: ServiceRecord[] }>(`/resources/${resourceId}/service-records`),
  logService: (resourceId: string, body: Record<string, unknown>) =>
    request<{ serviceRecord: ServiceRecord; resource: Resource }>(
      `/resources/${resourceId}/service-records`,
      { method: 'POST', body }
    ),
  updateService: (resourceId: string, recordId: string, body: Record<string, unknown>) =>
    request<{ serviceRecord: ServiceRecord }>(
      `/resources/${resourceId}/service-records/${recordId}`,
      { method: 'PATCH', body }
    ),

  sell: (resourceId: string, body: { soldDate: string; soldTo?: string | null }) =>
    request<{ resource: Resource }>(`/resources/${resourceId}/sell`, { method: 'POST', body }),

  serviceDue: () =>
    request<{ assets: AssetDue[]; thresholds: Thresholds }>('/resources/service-due'),
  setThresholds: (body: {
    serviceWarnKm: number;
    serviceWarnHours: number;
    reminderLeadDays: number;
  }) => request<{ thresholds: Thresholds }>('/resources/thresholds', { method: 'PATCH', body }),
};

// --- Machine types (round 28) ------------------------------------------

export interface MachineType {
  id: string;
  name: string;
  sortOrder: number;
  archived: boolean;
}

/**
 * The company's own list of what its machines ARE — the fine level of an
 * asset form's machine filter. Manager-only end to end, the GET included;
 * a worker's picker gets resolved names through the forms-scoped endpoints.
 */
export const machineTypes = {
  list: (options: { includeArchived?: boolean } = {}) =>
    request<{ machineTypes: MachineType[] }>(
      `/machine-types${options.includeArchived ? '?archived=true' : ''}`
    ),
  create: (body: { name: string }) =>
    request<{ machineType: MachineType }>('/machine-types', { method: 'POST', body }),
  update: (id: string, body: { name?: string; sortOrder?: number }) =>
    request<{ machineType: MachineType }>(`/machine-types/${id}`, { method: 'PATCH', body }),
  archive: (id: string, archived: boolean) =>
    request<{ machineType: MachineType }>(`/machine-types/${id}/archive`, {
      method: 'POST',
      body: { archived },
    }),
  remove: (id: string) => request<void>(`/machine-types/${id}`, { method: 'DELETE' }),
};
